import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline-primary'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  loading?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  children: ReactNode
}

const VARIANTS: Record<NonNullable<Props['variant']>, string> = {
  primary:         'bg-brand-500 hover:bg-brand-600 text-white',
  secondary:       'bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200',
  ghost:           'text-gray-600 hover:bg-gray-100',
  danger:          'bg-rose-600 hover:bg-rose-700 text-white',
  'outline-primary': 'bg-white text-brand-600 ring-1 ring-brand-300 hover:bg-brand-50',
}
// Tamanhos md/lg recebem `min-w-[160px]` por padrão (regra global) — todo
// botão de ação principal (Salvar/Adicionar/Atualizar/Aplicar/Confirmar) usa
// um desses tamanhos, garantindo presença visual mínima sem precisar lembrar.
const SIZES: Record<NonNullable<Props['size']>, string> = {
  xs: 'px-2 py-1 text-[11px] gap-1',
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-3.5 py-2 text-sm gap-2 min-w-[160px]',
  lg: 'px-4 py-2.5 text-sm gap-2 min-w-[180px]',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  iconLeft,
  iconRight,
  className,
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={loading || disabled}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-md transition cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading
        ? <Loader2 className="animate-spin" size={14} />
        : iconLeft}
      {children}
      {iconRight}
    </button>
  )
}
